import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { BrowserRouter, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import logoWhite from './assets/logo_ekynair_white.png';
import logoColor from './assets/logo_ekynair.png';
import './App.css';
import './styles/App.css';
import './styles/responsive.css';

import useAppState from './hooks/useAppState';
import { useUiMode } from './hooks/useUiMode';
import DarkModeToggle from './shared/DarkModeToggle';
import LanguageSwitcher from './shared/LanguageSwitcher';
import AppNav from './shared/AppNav';
import AppFooter from './shared/AppFooter';
import ConfirmModal from './shared/ConfirmModal';
import Login from './auth/LoginPage';
import Signup from './auth/SignupPage';
import ForgotPassword from './auth/ForgotPasswordPage';
import ResetPassword from './auth/ResetPasswordPage';
import PendingInvitations from './shared/PendingInvitations';
import PWAInstallBanner from './shared/PWAInstallBanner';
import ScrollToTopButton from './shared/ScrollToTopButton';
import TwoFactorPage from './auth/TwoFactorPage';
import { AuthProvider } from './contexts/AuthContext';
import { PAGES } from './constants/pages';
import { resolvePublicLegalRoute } from './routing/publicLegalRoutes';
// Sprint routeur Lot 0+1 — pages sans paramètre routées ; coexistence currentPage.
import OwnerRoutes from './routing/OwnerRoutes';
import { PAGE_TO_PATH, PATH_TO_PAGE, NON_ROUTED_PAGES } from './routing/ownerRouteMap';

// Code-split : pages chargées à la demande (PERF-07)
// NB : les pages SANS paramètre (LIST/HORSES/UPLOAD/NEW_HORSE/PROFILE/ADMIN/
// FEEDBACK/FAQ/NOTICE) sont désormais lazy-importées dans routing/OwnerRoutes.jsx
// (Sprint routeur Lot 0+1). Ne restent ici que les pages encore pilotées par
// currentPage (paramétrées + légales) + les écrans hors shell owner.
const SessionDashboard    = lazy(() => import('./sessions/SessionDashboard'));
const CompareSessionsPage = lazy(() => import('./sessions/CompareSessionsPage'));
const HorseEditModal      = lazy(() => import('./horses/HorseEditModal'));
const HealthJournal       = lazy(() => import('./horses/HealthJournal'));
const HorseDetail         = lazy(() => import('./horses/HorseDetail'));
const EkynAirLiveDashboard = lazy(() => import('./horses/EkynAirLiveDashboard'));
const CGUPage             = lazy(() => import('./settings/CGUPage'));
// Pack juridique v1.0 (§9 du DOCX)
const MentionsPage        = lazy(() => import('./settings/MentionsPage'));
const PrivacyPage         = lazy(() => import('./settings/PrivacyPage'));
const CookiesPage         = lazy(() => import('./settings/CookiesPage'));
const ModerationPage      = lazy(() => import('./settings/ModerationPage'));
const CGVPage             = lazy(() => import('./settings/CGVPage'));
const SupportPage         = lazy(() => import('./settings/SupportPage'));
// /profil + /au-revoir — entrées URL liées à l'anonymisation de compte
// (Apple 5.1.1(v) + RGPD art. 17). /profil est ouvert par l'app mobile depuis
// AboutScreen ; /au-revoir est la confirmation post-anonymisation.
const ProfilePage         = lazy(() => import('./profile/ProfilePage'));
const GoodbyePage         = lazy(() => import('./profile/GoodbyePage'));
// Mode Pro Vétérinaire — root monté uniquement si useUiMode() === 'vet_pro' (L4a).
const VetProRoot          = lazy(() => import('./vetpro/VetProRoot'));
// V1.1 vet context switcher — shell propriétaire allégé pour véto bascule.
const OwnerAsVetShell     = lazy(() => import('./vetpro/OwnerAsVetShell'));

// Background wrapper utilisé par les routes pathname-based (/profil, /au-revoir)
// — même look que le shell de l'app principale, sans header.
function bgWrapForRoute(darkMode) {
  return {
    minHeight: '100vh',
    background: darkMode
      ? '#1E1E32'
      : 'linear-gradient(135deg, #E6F0FF 0%, #F0F9FF 100%)',
  };
}

function App({ Router = BrowserRouter }) {
  // Wrapper : useAppState fournit la value du AuthProvider, qui doit
  // envelopper AppInner pour que les consommateurs de useAuth (useUiMode,
  // composants Mode Pro à venir) trouvent un contexte. Avant ce wrap, aucun
  // composant ne consommait useAuth réellement (les imports historiques en
  // tête de App.jsx n'étaient pas montés). L4a-0bis.
  //
  // Sprint routeur Lot 0 : <BrowserRouter> enveloppe le shell pour que AppInner
  // utilise useNavigate/useLocation. `Router` est injectable (défaut BrowserRouter
  // en prod ; les tests passent MemoryRouter pour ne pas dépendre de window.location).
  // useAppState reste appelé HORS routeur (aucun hook routeur dedans — navigate
  // vit uniquement dans AppInner / la couche routing).
  const s = useAppState();
  return (
    <Router>
      <AuthProvider value={s}>
        <AppInner s={s} />
      </AuthProvider>
    </Router>
  );
}

function AppInner({ s }) {
  const { t } = useTranslation();
  // Mode UI — owner par défaut. Bascule en 'vet_pro' UNIQUEMENT pour un véto
  // authentifié avec REACT_APP_VET_PRO_FEATURE_FLAG=true (cf. useUiMode L4-0,
  // Décision D9). Hook appelé inconditionnellement (règle des hooks) ; la
  // bascule effective se fait plus bas, après toutes les gardes auth.
  const uiMode = useUiMode();

  // ─── Sprint routeur Lot 0+1 — navigation routée + coexistence currentPage ──
  // (hooks routeur appelés inconditionnellement, AVANT toute garde/early-return).
  const navigate = useNavigate();
  const location = useLocation();
  // Onglet actif : déduit de l'URL pour les pages routées, de currentPage pour
  // les pages encore non routées (override legacy). Passé à AppNav qui reste
  // agnostique (il reçoit toujours une string + onNavigate).
  const activePage = NON_ROUTED_PAGES.has(s.currentPage)
    ? s.currentPage
    : (PATH_TO_PAGE[location.pathname] || PAGES.LIST);
  // Transition vers une page ROUTÉE : maj currentPage (coupe l'override legacy)
  // + navigate si l'URL diffère (évite une entrée d'historique en double).
  const goRouted = (page) => {
    s.setCurrentPage(page);
    const path = PAGE_TO_PATH[page];
    if (path && location.pathname !== path) navigate(path);
  };
  // Barre de nav / liens : side-effects de handleNavigate (loadSessions/loadHorses)
  // + navigate vers la route. Pour une page non routée, handleNavigate seul (état).
  const onNavigate = (page) => {
    s.handleNavigate(page);
    const path = PAGE_TO_PATH[page];
    if (path && location.pathname !== path) navigate(path);
  };

  // Header hide-on-scroll-down / show-on-scroll-up
  const [headerVisible, setHeaderVisible] = useState(true);
  const lastScrollY = useRef(0);
  useEffect(() => {
    let rafId = 0;
    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        const currentY = window.scrollY;
        if (currentY < 60) {
          setHeaderVisible(true); // toujours visible en haut de page
        } else if (currentY > lastScrollY.current + 5) {
          setHeaderVisible(false); // scroll vers le bas
        } else if (currentY < lastScrollY.current - 5) {
          setHeaderVisible(true);  // scroll vers le haut
        }
        lastScrollY.current = currentY;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); cancelAnimationFrame(rafId); };
  }, []);

  // ─── Routes publiques (légales + support) — bypass auth gate ────────
  // Map + résolution déléguées à routing/publicLegalRoutes (App Store 5.1.1 +
  // Google Play Data Safety : privacy/CGU/support accessibles sans auth).
  const publicLegalEl = resolvePublicLegalRoute(window.location.pathname, { darkMode: s.darkMode });
  if (publicLegalEl) return publicLegalEl;

  // /au-revoir — confirmation post-anonymisation, publique (pas d'auth requise).
  // Doit s'afficher même quand le user vient d'être logout (cookies invalidés).
  if (window.location.pathname === '/au-revoir') {
    return (
      <div className="App" style={{ minHeight: '100vh', background: s.darkMode ? '#1E1E32' : 'linear-gradient(135deg, #E6F0FF 0%, #F0F9FF 100%)' }}>
        <Suspense fallback={<div style={{ textAlign: 'center', padding: '60px', color: s.darkMode ? '#E8EDF2' : '#4A5568' }}>{t('common.loading')}</div>}>
          <GoodbyePage darkMode={s.darkMode} />
        </Suspense>
      </div>
    );
  }

  // /profil — page de gestion de compte. Auth requise : si pas connecté on
  // laisse la machine d'auth (login/2FA) tourner ; après login le pathname
  // est toujours /profil et ce check rend la page.
  const onProfilePath = window.location.pathname === '/profil';

  // ─── Auth pages ──────────────────────────────────────────────────────
  if (s.authPage === 'loading') return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: s.darkMode ? '#0D1117' : '#f0f4ff' }}>
      <div style={{ textAlign: 'center' }}>
        <img src={logoColor} alt="EkynAir" style={{ height: '48px', marginBottom: '24px', opacity: 0.85 }} />
        <div style={{ width: '28px', height: '28px', border: '3px solid #1B77F1',
          borderTopColor: 'transparent', borderRadius: '50%',
          animation: 'eky-spin 0.75s linear infinite', margin: '0 auto' }} />
        <style>{`@keyframes eky-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );

  // ─── 2FA en attente — prioritaire sur authPage pour éviter le court-circuit ──
  if (s.pending2FA) return <TwoFactorPage userId={s.pending2FA.userId} onSuccess={s.handle2FASuccess} darkMode={s.darkMode} />;

  // /profil rendu dès que l'utilisateur est authentifié (post-login compris).
  // On le vérifie ICI (avant les autres pages app) pour qu'il prenne le pas
  // sur l'état currentPage en localStorage.
  if (onProfilePath && s.currentUser) {
    return (
      <div className="App" style={bgWrapForRoute(s.darkMode)}>
        <Suspense fallback={<div style={{ textAlign: 'center', padding: '60px', color: s.darkMode ? '#E8EDF2' : '#4A5568' }}>{t('common.loading')}</div>}>
          <ProfilePage currentUser={s.currentUser} darkMode={s.darkMode} onLogout={s.handleLogout} />
        </Suspense>
      </div>
    );
  }

  if (s.authPage === 'login')  return <Login onLoginSuccess={s.handleLogin} on2FA={s.setPending2FA} onSwitchToSignup={() => s.setAuthPage('signup')} onForgotPassword={() => s.setAuthPage('forgot')} darkMode={s.darkMode} verificationMessage={s.verificationMessage} />;
  if (s.authPage === 'signup') return <Signup onSignupSuccess={() => s.setAuthPage('login')} onSwitchToLogin={() => s.setAuthPage('login')} darkMode={s.darkMode} />;
  if (s.authPage === 'forgot') return <ForgotPassword onBackToLogin={() => s.setAuthPage('login')} darkMode={s.darkMode} />;
  if (s.authPage === 'reset')  return <ResetPassword onSuccess={() => s.setAuthPage('login')} darkMode={s.darkMode} />;

  // ─── Bascule Mode Pro Vétérinaire (L4a) ──────────────────────────────
  // À ce stade, toutes les gardes auth/légales/2FA/profil sont passées :
  // l'utilisateur est authentifié et en mode app normal. Si useUiMode a
  // décidé 'vet_pro' (véto + flag activé), on monte VetProRoot et le shell
  // propriétaire (header + AppNav + écrans) n'est PAS monté du tout.
  if (uiMode === 'vet_pro') {
    return (
      <Suspense fallback={<div style={{ textAlign: 'center', padding: '60px', color: s.darkMode ? '#E8EDF2' : '#4A5568' }}>{t('common.loading')}</div>}>
        <VetProRoot s={s} />
      </Suspense>
    );
  }

  // ─── Bascule contexte véto V1.1 ──────────────────────────────────────
  // Symétrique à vet_pro : monte OwnerAsVetShell (shell allégé propriétaire
  // pour véto qui veut consulter ses Share entrants). Shell propriétaire
  // complet et VetProRoot non montés.
  if (uiMode === 'owner_as_vet') {
    return (
      <Suspense fallback={<div style={{ textAlign: 'center', padding: '60px', color: s.darkMode ? '#E8EDF2' : '#4A5568' }}>{t('common.loading')}</div>}>
        <OwnerAsVetShell s={s} />
      </Suspense>
    );
  }

  // ─── Layout helpers ──────────────────────────────────────────────────
  const bg = { minHeight: '100vh', background: s.darkMode ? '#1E1E32' : 'linear-gradient(135deg, #E6F0FF 0%, #F0F9FF 100%)' };

  const backBtn = (onClick, label = 'Chevaux') => (
    <div style={{ marginBottom: '16px' }}>
      <button type="button" onClick={onClick} style={{
        fontFamily: "'Inter', sans-serif", background: 'none', border: 'none',
        cursor: 'pointer', color: s.darkMode ? '#90CAF9' : '#1B77F1',
        fontSize: '13px', fontWeight: '600', padding: '6px 0',
        display: 'inline-flex', alignItems: 'center', gap: '5px'
      }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        {label}
      </button>
    </div>
  );

  const header = (clickable = false, nav = true) => (
    <header style={{
      background: s.darkMode ? 'linear-gradient(135deg, #0D2A5C 0%, #0A4A8A 100%)' : 'linear-gradient(135deg, #1B77F1 0%, #00CCFF 100%)',
      position: 'sticky', top: 0, zIndex: 1000,
      boxShadow: s.darkMode ? '0 2px 12px rgba(0,0,0,0.5)' : '0 2px 12px rgba(27,119,241,0.35)',
      transform: headerVisible ? 'translateY(0)' : 'translateY(-100%)',
      transition: 'transform 0.25s cubic-bezier(0.4,0,0.2,1)',
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 20px 6px' }}>
        {clickable ? (
          <button
            type="button" className="eky-logo-btn"
            onClick={() => onNavigate(PAGES.LIST)}
            aria-label={t('app.backHome')}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              transition: 'opacity 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.85'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
          >
            <img src={logoWhite} alt="EkynAir" className="eky-logo-img" />
          </button>
        ) : (
          <img src={logoWhite} alt="EkynAir" className="eky-logo-img" />
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          {process.env.REACT_APP_I18N_SWITCHER === 'true' && (
            <LanguageSwitcher darkMode={s.darkMode} />
          )}
          <DarkModeToggle darkMode={s.darkMode} setDarkMode={s.setDarkMode} />
        </div>
      </div>
      {nav && (
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <AppNav currentPage={activePage} darkMode={s.darkMode} onNavigate={onNavigate} inHeader currentUser={s.currentUser} />
        </div>
      )}
    </header>
  );

  // ─── Session dashboard view ──────────────────────────────────────────
  if (s.selectedSession) {
    return (
      <div className="App" style={bg}>
        {header(true)}
        <Suspense fallback={<div style={{ textAlign: 'center', padding: '60px', color: s.darkMode ? '#E8EDF2' : '#4A5568' }}>{t('common.loading')}</div>}>
        <main style={{ maxWidth: '1200px', margin: '40px auto', padding: '0 20px' }}>
          <SessionDashboard sessionId={s.selectedSession} darkMode={s.darkMode} currentUser={s.currentUser}
            getShareRole={s.getShareRole}
            onBack={() => { s.setSelectedSession(null); goRouted(PAGES.LIST); }}
            onViewHealth={(horseId) => s.openHorsePage(horseId, 'health', s.setHealthHorse)} />
        </main>
        </Suspense>
        <AppFooter darkMode={s.darkMode} />

        <ScrollToTopButton darkMode={s.darkMode} />
      </div>
    );
  }

  // ─── Main app ────────────────────────────────────────────────────────
  return (
    <div className="App" style={bg}>
      {header(true)}
      <Suspense fallback={<div style={{ textAlign: 'center', padding: '60px', color: s.darkMode ? '#E8EDF2' : '#4A5568' }}>{t('common.loading')}</div>}>
      <main style={{ maxWidth: '1200px', margin: '40px auto', padding: '0 20px' }}>

        {/* Invitations en attente — visible sur tous les onglets */}
        <PendingInvitations darkMode={s.darkMode} onAccepted={() => s.loadHorses()} />

        {/* Bannière PWA — proposée une fois, dismissable (localStorage) */}
        <PWAInstallBanner darkMode={s.darkMode} onOpenInstructions={() => onNavigate(PAGES.NOTICE)} />

        {/* ── Coexistence routeur / currentPage (Sprint routeur Lot 0+1) ──────
            Les pages encore pilotées par currentPage (paramétrées + légales) sont
            rendues par le switch legacy ci-dessous, qui PRIME sur <Routes> tant que
            currentPage ∈ NON_ROUTED_PAGES. Sinon, OwnerRoutes rend la page SANS
            paramètre selon l'URL. Frontière TEMPORAIRE — retirée aux Lots 2/3/5. */}
        {NON_ROUTED_PAGES.has(s.currentPage) ? (
          <>
            {s.currentPage === PAGES.LIVE && s.liveHorse && (
              <div>
                {backBtn(() => { s.setLiveHorse(null); goRouted(PAGES.HORSES); }, 'Mes chevaux')}
                <EkynAirLiveDashboard horseId={s.liveHorse._id} imei={s.liveHorse.equipmentId} horseName={s.liveHorse.horseName} horse={s.liveHorse} darkMode={s.darkMode} />
              </div>
            )}

            {s.currentPage === PAGES.HEALTH && s.healthHorse && (
              <HealthJournal horseId={s.healthHorse._id} horseName={s.healthHorse.horseName} darkMode={s.darkMode} shareRole={s.getShareRole()?.role || null}
                currentUser={s.currentUser}
                onBack={() => { s.setHealthHorse(null); goRouted(PAGES.HORSES); }} />
            )}

            {s.currentPage === PAGES.DETAIL && s.detailHorse && (
              <HorseDetail horse={s.detailHorse} darkMode={s.darkMode}
                currentUser={s.currentUser}
                share={s.getShareRole(s.detailHorse?._id)}
                onBack={() => { s.setDetailHorse(null); s.setShareRoleForHorse(null, null); goRouted(PAGES.HORSES); }}
                onEdit={(horseOrId) => s.handleEditHorse(horseOrId)}
                onViewSessions={(id) => {
                  const entry = s.getShareRole(id);
                  s.setSelectedHorse(id); s.loadSessions(id);
                  s.setShareRoleForHorse(id, entry?.role || null, entry?.acceptedAt || null);
                  goRouted(PAGES.LIST);
                }}
              />
            )}

            {s.currentPage === PAGES.CGU && (
              <CGUPage darkMode={s.darkMode} />
            )}

            {s.currentPage === PAGES.MENTIONS && (
              <MentionsPage darkMode={s.darkMode} onBack={() => goRouted(PAGES.PROFILE)} />
            )}

            {s.currentPage === PAGES.PRIVACY && (
              <PrivacyPage darkMode={s.darkMode} onBack={() => goRouted(PAGES.PROFILE)} />
            )}

            {s.currentPage === PAGES.COOKIES && (
              <CookiesPage darkMode={s.darkMode} onBack={() => goRouted(PAGES.PROFILE)} />
            )}

            {s.currentPage === PAGES.MODERATION && (
              <ModerationPage darkMode={s.darkMode} onBack={() => goRouted(PAGES.PROFILE)} />
            )}

            {s.currentPage === PAGES.CGV && (
              <CGVPage darkMode={s.darkMode} onBack={() => goRouted(PAGES.PROFILE)} />
            )}

            {s.currentPage === PAGES.SUPPORT && (
              <SupportPage darkMode={s.darkMode} onBack={() => goRouted(PAGES.PROFILE)} />
            )}

            {s.currentPage === PAGES.COMPARE && s.compareSessionIds.length >= 2 && (
              <CompareSessionsPage
                sessionIds={s.compareSessionIds}
                sessions={s.sessions}
                darkMode={s.darkMode}
                onBack={() => { s.setCompareSessionIds([]); goRouted(PAGES.LIST); }}
              />
            )}
          </>
        ) : (
          <OwnerRoutes s={s} onNavigate={onNavigate} goRouted={goRouted} />
        )}
      </main>
      </Suspense>

      <AppFooter darkMode={s.darkMode} />

      {s.editingHorse && (
        <Suspense fallback={
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999,
          }}>
            <div style={{
              color: '#fff',
              fontSize: '14px',
              padding: '20px',
              background: 'rgba(0,0,0,0.7)',
              borderRadius: '8px',
            }}>
              {t('common.loading')}
            </div>
          </div>
        }>
          <HorseEditModal horse={s.editingHorse} onClose={() => s.setEditingHorse(null)} onUpdate={s.handleHorseUpdated} darkMode={s.darkMode} currentUser={s.currentUser} share={s.getShareRole(s.editingHorse?._id)} />
        </Suspense>
      )}

      <ConfirmModal isOpen={s.confirmModal.isOpen} title={t('app.deleteSession.title')}
        message={t('app.deleteSession.message')}
        onConfirm={s.confirmDeleteSession} onCancel={() => s.setConfirmModal({ isOpen: false, sessionId: null })}
        darkMode={s.darkMode} confirmText={t('common.delete')} cancelText={t('common.cancel')} confirmColor="#EF4444" />

      <ConfirmModal isOpen={s.confirmHorseModal.isOpen} title={t('app.deleteHorse.title', { name: s.confirmHorseModal.horseName })}
        message={t('app.deleteHorse.message')}
        onConfirm={s.confirmDeleteHorse} onCancel={() => s.setConfirmHorseModal({ isOpen: false, horseId: null, horseName: '' })}
        darkMode={s.darkMode} confirmText={t('common.delete')} cancelText={t('common.cancel')} confirmColor="#EF4444" />

      {uiMode === 'owner' && <ScrollToTopButton darkMode={s.darkMode} />}
    </div>
  );
}


export default App;
